import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCompanies, useIndividuals } from "@/hooks/useCRM";
import { useGenerateCampaignFollowups, useSequenceTemplates } from "@/hooks/useCampaigns";
import type { CRMIndividual } from "@/types";

interface Props {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  onGenerated?: (result: {
    created_followup_count: number;
    skipped_existing_count: number;
    individuals_processed: number;
  }) => void;
}

/**
 * Pick a follow-up template + select contacts (filtered by search /
 * by company) → POST /campaigns/:id/generate-followups → done.
 *
 * Why pick-from-all-contacts rather than a dedicated "attendees" list:
 * we don't have an attendees table. The user's flow is "bulk-import
 * the webinar list (already shipped), then come here and select those
 * same names". A "show only contacts created in the last N days"
 * filter handles the common case of "I just imported them".
 */
export default function GenerateFollowupSequenceDialog({
  open,
  onClose,
  campaignId,
  onGenerated,
}: Props) {
  const { data: templates } = useSequenceTemplates();
  const { data: allIndividuals } = useIndividuals();
  const { data: companies } = useCompanies();
  const generate = useGenerateCampaignFollowups(campaignId);

  const [template, setTemplate] = useState("post_webinar_4_touch");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [recentOnly, setRecentOnly] = useState(true); // default: just-imported view
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset on each open so a previous draft doesn't leak.
  useEffect(() => {
    if (open) {
      setTemplate("post_webinar_4_touch");
      setCompanyFilter("");
      setSearch("");
      setRecentOnly(true);
      setSelected(new Set());
    }
  }, [open]);

  // Pin "now" once per dialog mount via useState's lazy initializer so
  // the useMemo below stays a pure function of its deps. Drift across a
  // day boundary while the dialog stays open would be cosmetic only
  // (a contact created 14d-1m ago would briefly remain in "Recent").
  const [sinceCutoff] = useState(() => Date.now() - 14 * 24 * 60 * 60 * 1000);
  const filtered: CRMIndividual[] = useMemo(() => {
    const list = allIndividuals ?? [];
    const since = recentOnly ? sinceCutoff : null;
    const searchLower = search.trim().toLowerCase();
    return list.filter((ind) => {
      if (companyFilter && ind.company_id !== companyFilter) return false;
      if (since && new Date(ind.created_at).getTime() < since) return false;
      if (searchLower) {
        const hay = `${ind.first_name} ${ind.last_name} ${ind.email ?? ""}`.toLowerCase();
        if (!hay.includes(searchLower)) return false;
      }
      return true;
    });
  }, [allIndividuals, companyFilter, search, recentOnly, sinceCutoff]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const ind of filtered) next.add(ind.id);
      return next;
    });
  };

  const clearAll = () => setSelected(new Set());

  const currentTemplate = templates?.find((t) => t.id === template);

  const submit = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const result = await generate.mutateAsync({ individualIds: ids, template });
      onGenerated?.(result);
      onClose();
    } catch {
      // Surface via the mutation.error block below.
    }
  };

  const errorDetail = generate.error
    ? ((generate.error as unknown as { response?: { data?: { detail?: string } } }).response?.data
        ?.detail ?? (generate.error as Error).message)
    : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Generate follow-up sequence</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorDetail && (
            <Alert severity="error" variant="outlined">
              {errorDetail}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={template}
              onChange={(e) => setTemplate(String(e.target.value))}
            >
              {templates?.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {currentTemplate && (
            <Box sx={{ p: 1.5, bgcolor: "#fafafa", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                This will create the following dated tasks per contact:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                {currentTemplate.touches.map((t) => (
                  <Chip
                    key={t.day_offset}
                    label={`Day ${t.day_offset}: ${t.label}`}
                    size="small"
                    color={t.priority === "high" ? "warning" : "default"}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Company</InputLabel>
              <Select
                label="Company"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(String(e.target.value))}
              >
                <MenuItem value="">All companies</MenuItem>
                {(companies ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              size="small"
              onClick={() => setRecentOnly((v) => !v)}
              variant={recentOnly ? "contained" : "outlined"}
            >
              {recentOnly ? "Showing: created last 14 days" : "Showing: all contacts"}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {selected.size} of {filtered.length} selected
            </Typography>
            <Button size="small" onClick={selectAllFiltered}>
              Select all
            </Button>
            <Button size="small" onClick={clearAll}>
              Clear
            </Button>
          </Stack>

          <Box
            sx={{
              maxHeight: 320,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <List dense disablePadding>
              {filtered.map((ind) => {
                const isSelected = selected.has(ind.id);
                return (
                  <ListItemButton key={ind.id} onClick={() => toggle(ind.id)} selected={isSelected}>
                    <Checkbox edge="start" checked={isSelected} tabIndex={-1} />
                    <ListItemText
                      primary={`${ind.first_name} ${ind.last_name}`}
                      secondary={[ind.email, ind.job_title].filter(Boolean).join(" · ")}
                    />
                  </ListItemButton>
                );
              })}
              {filtered.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No contacts match the filters. Try toggling the "recent" filter or clearing the
                    search.
                  </Typography>
                </Box>
              )}
            </List>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Each selected contact gets the full cadence above. Re-running with the same selection is
            safe — touches that already exist on this campaign are skipped, not duplicated.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generate.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={generate.isPending || selected.size === 0}
        >
          {generate.isPending
            ? "Generating…"
            : `Generate ${selected.size * (currentTemplate?.touches.length ?? 0)} tasks`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
